import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { usePwaInstall } from '../utils/pwaInstall';
import '../Install.css';

const APP = {
  name: 'ShreeFX',
  developer: 'ShreeFX Markets',
  domain: typeof window !== 'undefined' ? window.location.host : 'shreefx.app',
  category: 'Finance',
  rating: '4.9',
  reviews: '2.1K',
  downloads: '50K+',
  size: '12 MB',
  age: '18+',
  updated: 'Aug 2026',
};

const FEATURES = [
  {
    title: 'Live markets',
    copy: 'Real-time quotes and charts so you never trade on stale prices.',
  },
  {
    title: 'Positions & P&L',
    copy: 'Track open positions, net exposure, and performance in one place.',
  },
  {
    title: 'Instant on device',
    copy: 'Installs to your home screen — opens full-screen, stays updated automatically.',
  },
  {
    title: 'Secure sessions',
    copy: 'Fast login with session protection built for active traders.',
  },
];

const SCREENSHOTS = [
  { id: 'watch', label: 'Watchlist', tone: 'gold' },
  { id: 'pos', label: 'Positions', tone: 'emerald' },
  { id: 'trade', label: 'Trade ticket', tone: 'saffron' },
  { id: 'perf', label: 'Performance', tone: 'copper' },
];

const GUIDE = {
  'safari-ios': {
    title: 'Install on iPhone / iPad',
    steps: [
      'Tap the Share button (□↑) at the bottom',
      'Scroll down and tap “Add to Home Screen”',
      'Tap “Add” in the top-right corner',
    ],
  },
  'safari-mac': {
    title: 'Install on Mac',
    steps: [
      'Click File in the menu bar',
      'Choose “Add to Dock…”',
      'Click “Add”',
    ],
  },
  firefox: {
    title: 'Install on Firefox',
    steps: [
      'Tap the menu (⋮) in the top-right',
      'Tap “Install”',
      'Follow the on-screen prompt',
    ],
  },
  chromium: {
    title: 'Install',
    steps: [
      'Click the install icon (⊕) in the address bar',
      'Click “Install” in the popup',
    ],
  },
  other: {
    title: 'Install App',
    steps: [
      'Open your browser menu (⋮ or ≡)',
      'Look for “Install app” or “Add to Home Screen”',
      'Follow the on-screen prompt',
    ],
  },
};

const Install = () => {
  const [aboutOpen, setAboutOpen] = useState(false);
  const {
    isInstalled,
    canNativeInstall,
    installing,
    handleInstall,
    showGuide,
    setShowGuide,
    browserKind,
  } = usePwaInstall();

  const ctaLabel = useMemo(() => {
    if (isInstalled) return 'Open';
    if (installing) return 'Installing…';
    if (browserKind === 'safari-ios') return 'Add to Home Screen';
    return 'Install';
  }, [isInstalled, installing, browserKind]);

  const onInstallClick = async () => {
    if (isInstalled) {
      window.location.href = '/login';
      return;
    }
    // Kingdom FX pattern: prompt() when Chrome event exists, else show guide
    await handleInstall();
  };

  const guide = GUIDE[browserKind] || GUIDE.other;

  return (
    <div className="store-page" data-theme="light">
      <Helmet>
        <title>ShreeFX — Install</title>
        <meta name="description" content="Install ShreeFX — premium trading on your home screen." />
        <meta name="theme-color" content="#F7F4EC" />
      </Helmet>

      <header className="store-topbar">
        <div className="store-topbar-brand">
          <img src="/shreefx-icon.png" alt="" className="store-topbar-icon" />
          <span>ShreeFX</span>
        </div>
        <Link to="/login" className="store-topbar-link">
          Sign in
        </Link>
      </header>

      <main className="store-main">
        <section className="store-hero" aria-label="App details">
          <div className="store-hero-icon-wrap">
            <img src="/shreefx-icon.png" alt="ShreeFX app icon" className="store-hero-icon" />
          </div>
          <div className="store-hero-copy">
            <h1 className="store-app-name">{APP.name}</h1>
            <p className="store-developer">{APP.developer}</p>
            <p className="store-contains">Finance · Trading</p>
            <div className="store-verified">
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 2L4 5v6c0 5.25 3.4 10.15 8 11.35C16.6 21.15 20 16.25 20 11V5l-8-3zm-1.1 14.2l-3.6-3.6 1.4-1.4 2.2 2.2 4.5-4.5 1.4 1.4-5.9 5.9z"
                />
              </svg>
              {canNativeInstall
                ? 'Ready to install on this device'
                : 'Installs to your device · No app store required'}
            </div>
          </div>
        </section>

        <section className="store-actions">
          <button
            type="button"
            className={`store-install-btn ${isInstalled ? 'is-open' : ''}`}
            onClick={onInstallClick}
            disabled={installing}
          >
            {ctaLabel}
          </button>
          <button
            type="button"
            className="store-share-btn"
            aria-label="Share"
            onClick={() => {
              if (navigator.share) {
                navigator
                  .share({
                    title: 'ShreeFX',
                    text: 'Install ShreeFX trading app',
                    url: window.location.href,
                  })
                  .catch(() => {});
              } else {
                navigator.clipboard?.writeText(window.location.href);
              }
            }}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path
                fill="currentColor"
                d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"
              />
            </svg>
          </button>
        </section>

        <section className="store-stats" aria-label="App stats">
          <div className="store-stat">
            <div className="store-stat-value">
              {APP.rating}
              <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 17.3l6.18 3.7-1.64-7.03L21 9.24l-7.19-.62L12 2 10.19 8.62 3 9.24l4.46 4.73L5.82 21z"
                />
              </svg>
            </div>
            <div className="store-stat-label">{APP.reviews} reviews</div>
          </div>
          <div className="store-stat-divider" />
          <div className="store-stat">
            <div className="store-stat-value">{APP.downloads}</div>
            <div className="store-stat-label">Downloads</div>
          </div>
          <div className="store-stat-divider" />
          <div className="store-stat">
            <div className="store-stat-value store-stat-age">{APP.age}</div>
            <div className="store-stat-label">Rated for {APP.age}</div>
          </div>
        </section>

        <section className="store-shots" aria-label="Screenshots">
          <div className="store-shots-track">
            {SCREENSHOTS.map((shot) => (
              <figure key={shot.id} className={`store-phone tone-${shot.tone}`}>
                <div className="store-phone-notch" />
                <div className="store-phone-screen">
                  <div className="store-phone-brand">ShreeFX</div>
                  <div className="store-phone-title">{shot.label}</div>
                  <div className="store-phone-rows">
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="store-phone-bars">
                    <i />
                    <i />
                    <i />
                  </div>
                </div>
                <figcaption className="store-phone-caption">{shot.label}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="store-section">
          <button
            type="button"
            className="store-section-head"
            onClick={() => setAboutOpen((v) => !v)}
            aria-expanded={aboutOpen}
          >
            <h2>About this app</h2>
            <svg
              className={`store-chevron ${aboutOpen ? 'open' : ''}`}
              viewBox="0 0 24 24"
              width="22"
              height="22"
              aria-hidden="true"
            >
              <path fill="currentColor" d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
            </svg>
          </button>
          <p className={`store-about ${aboutOpen ? 'is-open' : ''}`}>
            ShreeFX is a lightning-fast trading app that installs directly on your device — no app store wait,
            no bloat. Get live markets, real-time charts, positions, and your full trading dashboard from the
            home screen. Works like a native app, loads instantly, and stays up to date automatically.
          </p>
          <div className="store-tags">
            <span>{APP.category}</span>
            <span>Trading</span>
            <span>Charts</span>
            <span>Portfolio</span>
          </div>
          <div className="store-meta-row">
            <span>Updated {APP.updated}</span>
            <span>{APP.size}</span>
            <span>{APP.domain}</span>
          </div>
        </section>

        <section className="store-section">
          <h2 className="store-section-title">What you get</h2>
          <ul className="store-features">
            {FEATURES.map((f) => (
              <li key={f.title}>
                <div className="store-feature-dot" aria-hidden="true" />
                <div>
                  <strong>{f.title}</strong>
                  <p>{f.copy}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="store-section store-safety">
          <h2 className="store-section-title">Data safety</h2>
          <p>
            Account credentials stay on secure servers. The installed app uses the same protected sessions as
            the web experience. You can remove ShreeFX anytime from your device settings.
          </p>
        </section>

        <section className="store-footer-cta">
          <p>Already installed?</p>
          <Link to="/login" className="store-open-link">
            Open ShreeFX
          </Link>
        </section>
      </main>

      {showGuide && (
        <div className="store-sheet-backdrop" role="presentation" onClick={() => setShowGuide(false)}>
          <div
            className="store-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="store-guide-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="store-sheet-handle" />
            <h3 id="store-guide-title">{guide.title}</h3>
            <ol className="store-guide-steps">
              {guide.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <button type="button" className="store-install-btn" onClick={() => setShowGuide(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Install;
